import { useState, useRef, useEffect } from 'react';
import { api } from '../../api';
import { usePage } from '../../content/PageContext';
import { resolveUrl } from '../../content/assets';
import { renderBlock } from '../../components/PageRenderer';
import '../../builder.css';
import '../../pages.css';

const uid = (p) => p + '-' + Math.random().toString(36).slice(2, 8);

const BLOCK_TYPES = [
  { type: 'heading', label: '标题' },
  { type: 'text', label: '文本' },
  { type: 'image', label: '图片' },
  { type: 'button', label: '按钮' },
  { type: 'video', label: '视频' },
  { type: 'spacer', label: '间距' },
];

const BLOCK_LABEL = { heading: '标题', text: '文本', image: '图片', button: '按钮', video: '视频', spacer: '间距' };

const LAYOUTS = [
  { label: '1 列', spans: [12] },
  { label: '2 列', spans: [6, 6] },
  { label: '3 列', spans: [4, 4, 4] },
  { label: '2/3+1/3', spans: [8, 4] },
  { label: '1/3+2/3', spans: [4, 8] },
];

function emptyBlock(type) {
  switch (type) {
    case 'heading': return { type, text: '', level: 'h2', align: 'left' };
    case 'text': return { type, text: '', align: 'left' };
    case 'image': return { type, url: '', alt: '', shape: 'square', align: 'left' };
    case 'button': return { type, text: '', link: '', align: 'left' };
    case 'video': return { type, url: '', align: 'left' };
    case 'spacer': return { type, height: 40 };
    default: return { type: 'text', text: '' };
  }
}

function F({ label, children }) {
  return <div className="pb-field"><label>{label}</label>{children}</div>;
}

function ColorField({ value, onChange }) {
  const hex = /^#[0-9a-fA-F]{6}$/.test(value || '') ? value : '#000000';
  return (
    <div className="color-field">
      <input type="color" value={hex} onChange={(e) => onChange(e.target.value)} />
      <input type="text" value={value || ''} placeholder="#hex" onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

const pct = (span) => Math.round(((span || 12) / 12) * 100) + '%';

// --- recursive tree transforms (by id) ---
function txSection(s, id, fn, remove) {
  if (s.id === id) return remove ? null : fn(s);
  return { ...s, rows: (s.rows || []).map((r) => txRow(r, id, fn, remove)).filter(Boolean) };
}
function txRow(r, id, fn, remove) {
  if (r.id === id) return remove ? null : fn(r);
  return { ...r, columns: (r.columns || []).map((c) => txCol(c, id, fn, remove)).filter(Boolean) };
}
function txCol(c, id, fn, remove) {
  if (c.id === id) return remove ? null : fn(c);
  return {
    ...c,
    blocks: (c.blocks || []).map((b) => (b.id === id ? (remove ? null : fn(b)) : b)).filter(Boolean),
    rows: (c.rows || []).map((r) => txRow(r, id, fn, remove)).filter(Boolean),
  };
}
function updateById(sections, id, fn) {
  return sections.map((s) => txSection(s, id, fn, false)).filter(Boolean);
}
function removeById(sections, id) {
  return sections.map((s) => txSection(s, id, null, true)).filter(Boolean);
}
function moveNode(sections, id, dir) {
  function moveIn(arr) {
    const i = arr.findIndex((x) => x.id === id);
    if (i >= 0) {
      const ni = i + dir;
      if (ni < 0 || ni >= arr.length) return arr;
      const next = [...arr];
      [next[i], next[ni]] = [next[ni], next[i]];
      return next;
    }
    return arr.map(recurse);
  }
  function recurse(node) {
    let n = node;
    if (node.columns) n = { ...n, columns: moveIn(node.columns) };
    if (node.blocks) n = { ...n, blocks: moveIn(node.blocks) };
    if (node.rows) n = { ...n, rows: moveIn(node.rows) };
    return n;
  }
  return moveIn(sections);
}
function findNode(sections, id) {
  let result = null;
  function walk(node, type) {
    if (result) return;
    if (node && node.id === id) { result = { node, type }; return; }
    if (type === 'section') (node.rows || []).forEach((r) => walk(r, 'row'));
    else if (type === 'row') (node.columns || []).forEach((c) => walk(c, 'column'));
    else if (type === 'column') {
      (node.blocks || []).forEach((b) => walk(b, 'block'));
      (node.rows || []).forEach((r) => walk(r, 'row'));
    }
  }
  (sections || []).forEach((s) => walk(s, 'section'));
  return result;
}

export default function PageBuilder() {
  const { page, loaded, save } = usePage();
  const [sections, setSections] = useState(page.sections || []);
  const [sel, setSel] = useState({ type: 'section', id: null });
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [videoProgress, setVideoProgress] = useState(-1);
  const inited = useRef(false);

  useEffect(() => {
    if (loaded && !inited.current) {
      setSections(page.sections || []);
      setSel({ type: 'section', id: page.sections[0] ? page.sections[0].id : null });
      setActiveSectionId(page.sections[0] ? page.sections[0].id : null);
      inited.current = true;
    }
  }, [loaded, page.sections]);

  const apply = (id, fn) => setSections((prev) => updateById(prev, id, fn));
  const remove = (id) => {
    if (!window.confirm('删除该元素？')) return;
    setSections((prev) => removeById(prev, id));
    if (sel.id === id) setSel({ type: 'section', id: null });
  };
  const move = (id, dir) => setSections((prev) => moveNode(prev, id, dir));

  async function uploadImage(file) {
    const { uploadUrl, publicUrl } = await api.uploadUrl(file.name, file.type, 'images');
    await api.putFile(uploadUrl, file);
    return publicUrl;
  }
  async function handleImageUpload(file, applyUrl) {
    if (!file) return;
    try {
      const url = await uploadImage(file);
      applyUrl(url);
    } catch (e) {
      setMsg('图片上传失败：' + e.message);
    }
  }
  async function uploadVideo(file, onProgress) {
    const { uploadUrl, publicUrl } = await api.uploadUrl(file.name, file.type, 'page-videos');
    await api.putFile(uploadUrl, file, onProgress);
    return publicUrl;
  }

  function addSection() {
    const id = uid('sec');
    setSections((prev) => [...prev, {
      id, title: '新区块', icon: '',
      rows: [{ id: uid('row'), columns: [{ id: uid('col'), span: 12, blocks: [{ id: uid('blk'), type: 'text', text: '在此输入内容…' }] }] }],
    }]);
    setSel({ type: 'section', id });
    setActiveSectionId(id);
  }
  function moveSection(id, dir) {
    setSections((prev) => {
      const i = prev.findIndex((s) => s.id === id);
      const ni = i + dir;
      if (i < 0 || ni < 0 || ni >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[ni]] = [next[ni], next[i]];
      return next;
    });
  }
  function addRow(sectionId) {
    apply(sectionId, (s) => ({ ...s, rows: [...(s.rows || []), { id: uid('row'), columns: [{ id: uid('col'), span: 12, blocks: [] }] }] }));
  }
  function applyLayout(rowId, spans) {
    apply(rowId, (r) => ({ ...r, columns: spans.map((sp) => ({ id: uid('col'), span: sp, blocks: [] })) }));
  }
  function addColumn(rowId) {
    apply(rowId, (r) => ({ ...r, columns: [...(r.columns || []), { id: uid('col'), span: 6, blocks: [] }] }));
  }
  function addNestedRow(columnId) {
    apply(columnId, (c) => ({ ...c, rows: [...(c.rows || []), { id: uid('row'), columns: [{ id: uid('col'), span: 12, blocks: [] }] }] }));
  }
  function addBlock(columnId, type) {
    apply(columnId, (c) => ({ ...c, blocks: [...(c.blocks || []), { ...emptyBlock(type), id: uid('blk') }] }));
  }

  async function onSave() {
    setSaving(true); setMsg('');
    try {
      await save(sections);
      setMsg('已保存并发布');
    } catch (e) {
      setMsg('保存失败：' + e.message);
    } finally { setSaving(false); }
  }

  function renderRow(r) {
    const gap = r.gap || 20;
    const vAlign = r.valign === 'top' ? 'flex-start' : r.valign === 'middle' ? 'center' : r.valign === 'bottom' ? 'flex-end' : undefined;
    return (
      <div className="pb-editor-row" key={r.id} onClick={(e) => { e.stopPropagation(); setSel({ type: 'row', id: r.id }); }}>
        <div className={'pb-editor-row-head' + (sel.type === 'row' && sel.id === r.id ? ' selected' : '')}>
          <span>行</span>
        </div>
        <div className="pb-editor-cols" style={{ alignItems: vAlign }}>
          {(r.columns || []).map((c) => renderCol(c, gap))}
        </div>
      </div>
    );
  }

  function renderCol(c, gap) {
    return (
      <div
        key={c.id}
        className={'pb-editor-col' + (sel.type === 'column' && sel.id === c.id ? ' selected' : '')}
        style={{ width: `calc(${((c.span || 12) / 12) * 100}% - ${gap * (12 - (c.span || 12)) / 12}px)`, backgroundColor: c.bg || undefined, textAlign: c.align || undefined }}
        onClick={(e) => { e.stopPropagation(); setSel({ type: 'column', id: c.id }); }}
      >
        <div className="pb-editor-col-head">
          <span>列 · {pct(c.span)}</span>
        </div>
        {(c.blocks || []).map((b) => (
          <div
            key={b.id}
            className={'pb-editor-block' + (sel.type === 'block' && sel.id === b.id ? ' selected' : '')}
            onClick={(e) => { e.stopPropagation(); setSel({ type: 'block', id: b.id }); }}
          >
            <div className="pb-editor-block-head"><span>{BLOCK_LABEL[b.type] || b.type}</span></div>
            <div className="pb-block-preview">{renderBlock(b)}</div>
          </div>
        ))}
        {(c.rows || []).map((r) => renderRow(r))}
      </div>
    );
  }

  function reorderButtons(id) {
    return (
      <span className="pb-reorder">
        <button className="mini-btn" onClick={() => move(id, -1)}>↑</button>
        <button className="mini-btn" onClick={() => move(id, 1)}>↓</button>
      </span>
    );
  }

  function inspector() {
    const found = findNode(sections, sel.id);
    if (!found) return <p className="muted">在中间画布点击一个区块 / 行 / 列以编辑。</p>;
    const { node, type } = found;

    if (type === 'section') {
      const s = node;
      const patch = (p) => apply(s.id, (x) => ({ ...x, ...p }));
      return (
        <>
          <F label="标题"><input value={s.title || ''} onChange={(e) => patch({ title: e.target.value })} /></F>
          <F label="图标 (URL/asset:)"><input value={s.icon || ''} onChange={(e) => patch({ icon: e.target.value })} /></F>
          <F label="锚点 id"><input value={s.id || ''} onChange={(e) => patch({ id: e.target.value })} /></F>
          <F label="背景色"><ColorField value={s.bg} onChange={(v) => patch({ bg: v })} /></F>
          <F label="文字色"><ColorField value={s.color} onChange={(v) => patch({ color: v })} /></F>
          <F label="内边距"><input value={s.pad || ''} placeholder="如 40px 0" onChange={(e) => patch({ pad: e.target.value })} /></F>
          <F label="背景图 (URL/asset:)"><input value={s.bgImage || ''} onChange={(e) => patch({ bgImage: e.target.value })} /></F>
          <F label="背景图透明度"><input type="number" min="0" max="1" step="0.05" value={s.bgOpacity != null ? s.bgOpacity : 0.15} onChange={(e) => patch({ bgOpacity: Number(e.target.value) })} /></F>
          <div className="pb-inspector-actions">
            <button className="btn" onClick={() => addRow(s.id)}>+ 添加行</button>
          </div>
        </>
      );
    }

    if (type === 'row') {
      const r = node;
      return (
        <>
          <F label="列布局">
            <div className="pb-layout-picker">
              {LAYOUTS.map((l) => <button key={l.label} className="mini-btn" onClick={() => applyLayout(r.id, l.spans)}>{l.label}</button>)}
            </div>
          </F>
          <F label="列间距 (px)"><input type="number" value={r.gap || 20} onChange={(e) => apply(r.id, (x) => ({ ...x, gap: Number(e.target.value) }))} /></F>
          <F label="列垂直对齐">
            <select
              value={r.valign || 'stretch'}
              onChange={(e) => apply(r.id, (x) => { const n = { ...x }; if (e.target.value === 'stretch') delete n.valign; else n.valign = e.target.value; return n; })}
            >
              <option value="stretch">等高（默认）</option>
              <option value="top">顶部</option>
              <option value="middle">中部</option>
              <option value="bottom">底部</option>
            </select>
          </F>
          <div className="pb-inspector-actions">
            {reorderButtons(r.id)}
            <button className="btn" onClick={() => addColumn(r.id)}>+ 列</button>
            <button className="btn secondary" onClick={() => remove(r.id)}>删除行</button>
          </div>
        </>
      );
    }

    if (type === 'column') {
      const c = node;
      const patch = (p) => apply(c.id, (x) => ({ ...x, ...p }));
      return (
        <>
          <F label="列宽">
            <select value={c.span || 12} onChange={(e) => patch({ span: Number(e.target.value) })}>
              {[3, 4, 6, 8, 12].map((sp) => <option key={sp} value={sp}>{pct(sp)}</option>)}
            </select>
          </F>
          <F label="背景色"><ColorField value={c.bg} onChange={(v) => patch({ bg: v })} /></F>
          <F label="内边距"><input value={c.pad || ''} placeholder="如 20px" onChange={(e) => patch({ pad: e.target.value })} /></F>
          <F label="圆角"><input value={c.radius || ''} placeholder="如 5px" onChange={(e) => patch({ radius: e.target.value })} /></F>
          <F label="对齐">
            <select value={c.align || 'left'} onChange={(e) => patch({ align: e.target.value })}>
              <option value="left">左</option><option value="center">中</option><option value="right">右</option>
            </select>
          </F>
          <F label="阴影"><input type="checkbox" checked={!!c.shadow} onChange={(e) => patch({ shadow: e.target.checked })} /></F>
          <F label="添加元素">
            <div className="pb-layout-picker">
              {BLOCK_TYPES.map((bt) => <button key={bt.type} className="mini-btn" onClick={() => addBlock(c.id, bt.type)}>+ {bt.label}</button>)}
            </div>
          </F>
          <div className="pb-inspector-actions">
            {reorderButtons(c.id)}
            <button className="btn" onClick={() => addNestedRow(c.id)}>+ 嵌套行</button>
            <button className="btn secondary" onClick={() => remove(c.id)}>删除列</button>
          </div>
        </>
      );
    }

    if (type === 'block') {
      const b = node;
      const patch = (p) => apply(b.id, (x) => ({ ...x, ...p }));
      const alignSel = (
        <F label="对齐">
          <select value={b.align || 'left'} onChange={(e) => patch({ align: e.target.value })}>
            <option value="left">左</option><option value="center">中</option><option value="right">右</option>
          </select>
        </F>
      );
      let body;
      switch (b.type) {
        case 'heading':
          body = (
            <>
              <F label="文字"><input value={b.text || ''} onChange={(e) => patch({ text: e.target.value })} /></F>
              <F label="级别"><select value={b.level || 'h2'} onChange={(e) => patch({ level: e.target.value })}><option value="h1">H1</option><option value="h2">H2</option><option value="h3">H3</option><option value="h4">H4</option></select></F>
              {alignSel}
              <F label="颜色"><ColorField value={b.color} onChange={(v) => patch({ color: v })} /></F>
              <F label="字号"><input value={b.size || ''} placeholder="如 32px" onChange={(e) => patch({ size: e.target.value })} /></F>
              <F label="背景色"><ColorField value={b.bg} onChange={(v) => patch({ bg: v })} /></F>
              <F label="内边距"><input value={b.pad || ''} placeholder="如 20px" onChange={(e) => patch({ pad: e.target.value })} /></F>
              <F label="圆角"><input value={b.radius || ''} placeholder="如 20px" onChange={(e) => patch({ radius: e.target.value })} /></F>
            </>
          );
          break;
        case 'text':
          body = (
            <>
              <F label="文字"><textarea value={b.text || ''} onChange={(e) => patch({ text: e.target.value })} /></F>
              {alignSel}
              <F label="颜色"><ColorField value={b.color} onChange={(v) => patch({ color: v })} /></F>
              <F label="字号"><input value={b.size || ''} placeholder="如 20px" onChange={(e) => patch({ size: e.target.value })} /></F>
            </>
          );
          break;
        case 'image':
          body = (
            <>
              {b.url && (
                <div className="pb-inspector-preview" style={{ display: 'flex', justifyContent: b.align === 'center' ? 'center' : b.align === 'right' ? 'flex-end' : 'flex-start' }}>
                  <img className="pb-inspector-img" src={resolveUrl(b.url)} alt={b.alt || ''} style={{ width: b.width || undefined }} />
                </div>
              )}
              <F label="上传"><input type="file" accept="image/*" onChange={(e) => handleImageUpload(e.target.files[0], (url) => patch({ url }))} /></F>
              <F label="URL 或 asset:"><input value={b.url || ''} onChange={(e) => patch({ url: e.target.value })} /></F>
              <F label="替代文字"><input value={b.alt || ''} onChange={(e) => patch({ alt: e.target.value })} /></F>
              {alignSel}
              <F label="形状"><select value={b.shape || 'square'} onChange={(e) => patch({ shape: e.target.value })}><option value="square">方形</option><option value="circle">圆形</option></select></F>
              <F label="填充"><select value={b.fit || ''} onChange={(e) => patch({ fit: e.target.value })}><option value="">默认</option><option value="cover">裁切铺满</option><option value="contain">完整显示</option></select></F>
              <F label="宽"><input value={b.width || ''} placeholder="如 100% 或 200px" onChange={(e) => patch({ width: e.target.value })} /></F>
              <F label="高"><input value={b.height || ''} placeholder="如 200px（可选）" onChange={(e) => patch({ height: e.target.value })} /></F>
            </>
          );
          break;
        case 'button':
          body = (
            <>
              <F label="文字"><input value={b.text || ''} onChange={(e) => patch({ text: e.target.value })} /></F>
              <F label="链接"><input value={b.link || ''} placeholder="#about 或 https://…" onChange={(e) => patch({ link: e.target.value })} /></F>
              {alignSel}
              <F label="背景色"><ColorField value={b.bg} onChange={(v) => patch({ bg: v })} /></F>
              <F label="文字色"><ColorField value={b.color} onChange={(v) => patch({ color: v })} /></F>
            </>
          );
          break;
        case 'video':
          body = (
            <>
              {b.url && (
                <div className="pb-inspector-preview">
                  <video controls src={resolveUrl(b.url)} style={{ maxWidth: '100%', maxHeight: 160 }} />
                </div>
              )}
              <F label="上传视频">
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => {
                    const f = e.target.files[0];
                    if (!f) return;
                    setVideoProgress(0);
                    uploadVideo(f, setVideoProgress)
                      .then((url) => patch({ url }))
                      .catch((err) => setMsg('视频上传失败：' + err.message))
                      .finally(() => setVideoProgress(-1));
                  }}
                />
                {videoProgress >= 0 && <p className="muted">上传中 {videoProgress}%</p>}
              </F>
              <F label="或视频 URL"><input value={b.url || ''} placeholder="https://… 或 YouTube" onChange={(e) => patch({ url: e.target.value })} /></F>
              {alignSel}
            </>
          );
          break;
        case 'spacer':
          body = <F label="高度 (px)"><input type="number" value={b.height != null ? b.height : 40} onChange={(e) => patch({ height: Number(e.target.value) })} /></F>;
          break;
        default:
          body = null;
      }
      return (
        <>
          <div className="pb-inspector-actions">
            {reorderButtons(b.id)}
            <button className="btn secondary" onClick={() => remove(b.id)}>删除</button>
          </div>
          {body}
        </>
      );
    }
    return <p className="muted">在中间画布点击一个区块 / 行 / 列以编辑。</p>;
  }

  const activeSection = sections.find((s) => s.id === activeSectionId);

  return (
    <div className="pb-builder">
      <div className="pb-left">
        <div className="pb-pane-title">页面区块</div>
        <button className="btn" onClick={addSection}>+ 新增区块</button>
        <div className="pb-outline">
          {sections.map((s, si) => (
            <div
              key={s.id}
              className={'pb-outline-item' + (sel.type === 'section' && sel.id === s.id ? ' active' : '')}
              onClick={() => { setActiveSectionId(s.id); setSel({ type: 'section', id: s.id }); }}
            >
              <span>{s.title || ('区块 ' + (si + 1))}</span>
              <span className="pb-outline-ops">
                <button className="mini-btn" onClick={(e) => { e.stopPropagation(); moveSection(s.id, -1); }}>↑</button>
                <button className="mini-btn" onClick={(e) => { e.stopPropagation(); moveSection(s.id, 1); }}>↓</button>
                <button className="mini-btn danger" onClick={(e) => { e.stopPropagation(); remove(s.id); }}>×</button>
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="pb-canvas">
        {activeSection ? (
          <>
            <div className="pb-canvas-head">
              <strong>{activeSection.title || '区块'}</strong>
            </div>
            {(activeSection.rows || []).map((r) => renderRow(r))}
          </>
        ) : (
          <p className="muted">点击左上「新增区块」开始。</p>
        )}
      </div>

      <div className="pb-right">
        <div className="pb-pane-title">属性设定</div>
        {inspector()}
        <div className="pb-inspector-save">
          <button className="btn primary" onClick={onSave} disabled={saving}>{saving ? '保存中…' : '保存变更'}</button>
          {msg && <span className={msg.startsWith('保存失败') || msg.startsWith('图片上传失败') ? 'error' : 'success'}>{msg}</span>}
        </div>
      </div>
    </div>
  );
}
